
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    LayoutDashboard,
    Users,
    FileText,
    PieChart as PieChartIcon,
    Search,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    ShieldCheck,
    ExternalLink,
    AlertCircle,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Filter,
    CheckCircle2,
    XCircle,
    RotateCcw
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const SHEET_ID = '1P9UhjwUAY7rUMc3kBb6WYcfJ5C8dA_t2WajeJhtb-zA';
const GOOGLE_SHEET_JSON_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json`;

const COLUMN_MAPPING = {
    "วันที่สร้างรายการ": "createdAt",
    "วันที่ส่งคำขอ": "requestDate",
    "Partner": "partner",
    "เลขที่รับแจ้ง": "notificationNo",
    "วันที่ซื้อประกัน": "purchaseDate",
    "เลขที่กรมธรรม์": "policyNo",
    "วันที่กรมธรรม์มีผลบังคับ": "effectiveDate",
    "วันที่กรมธรรม์หมดอายุ": "expiryDate",
    "เบี้ยประกันภัยรวมตามกรมธรรม์": "totalPremium",
    "เบี้ยประกันสุทธิ": "netPremium",
    "เบี้ยประกันภัยรวมขายจริง": "actualSellingPrice",
    "Product": "product",
    "จำนวนงวด": "installments",
    "%เงินดาวน์": "downPaymentPercent",
    "ชำระงวดแรก(ดาวน์)": "firstInstallment",
    "จำนวนงวดที่ผ่อน": "installmentCount",
    "จำนวนเงินผ่อนต่องวด": "installmentAmount",
    "บริษัทประกัน": "insuranceCompany",
    "ประเภทของประกัน": "insuranceType",
    "ยี่ห้อรถ": "carBrand",
    "รุ่นรถ": "carModel",
    "ปีที่จดทะเบียนรถยนต์": "registrationYear",
    "ทะเบียนรถ": "licensePlate",
    "ประเภทรถ": "vehicleType",
    "ตรวจสอบเอกสาร": "docCheck",
    "สาเหตุ": "reason",
    "Welcome Call": "welcomeCall",
    "ข้อมูลจาก": "dataSource",
    "ชื่อ-สกุล Agent": "agentName",
    "Status": "status",
    "งวดที่": "term",
    "วันที่ครบกำหนดจ่าย": "dueDate",
    "ยอดเงิน": "amount",
    "Payment": "paymentStatus",
    "วันที่จ่าย": "paymentDate",
    "remark": "remark"
};

const DEFAULT_STATUS_SELECTION = [
    'UNQ(คุณสมบัติไม่ผ่าน)',
    'ตกค้าง(รอตรวจสอบ)',
    'ตกค้าง(ร่าง)',
    'รอตรวจสอบ'
];

const parseDate = (dateStr) => {
    if (!dateStr) return 0;
    const parts = dateStr.split(' ');
    const dateParts = parts[0].split('/');
    if (dateParts.length < 3) return 0;
    const d = parseInt(dateParts[0]);
    const m = parseInt(dateParts[1]) - 1;
    const y = parseInt(dateParts[2]);

    if (parts[1]) {
        const timeParts = parts[1].split(':');
        const hh = parseInt(timeParts[0]) || 0;
        const mm = parseInt(timeParts[1]) || 0;
        const ss = parseInt(timeParts[2]) || 0;
        return new Date(y, m, d, hh, mm, ss).getTime();
    }
    return new Date(y, m, d).getTime();
};

const sortRecords = (records, key, direction) => {
    return [...records].sort((a, b) => {
        let valA = a[key];
        let valB = b[key];

        if (key === 'createdAt' || key === 'dueDate' || key === 'paymentDate') {
            valA = parseDate(String(valA));
            valB = parseDate(String(valB));
        }

        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
    });
};

const SortableHeader = ({ label, sortKey, currentSort, onSort }) => {
    const isActive = currentSort.key === sortKey;
    return (
        <th
            className="px-6 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors group"
            onClick={() => onSort(sortKey)}
        >
            <div className="flex items-center gap-1">
                {label}
                <span className="text-slate-300 group-hover:text-slate-500 transition-colors">
                    {!isActive && <ArrowUpDown size={12} />}
                    {isActive && (currentSort.dir === 'asc' ? <ArrowUp size={12} className="text-blue-500" /> : <ArrowDown size={12} className="text-blue-500" />)}
                </span>
            </div>
        </th>
    );
};

const SummaryCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-center gap-4">
        <div className={`${color} p-3 rounded-lg text-white`}>
            <Icon size={24} />
        </div>
        <div>
            <p className="text-sm text-slate-500 font-medium">{title}</p>
            <h3 className="text-2xl font-bold text-slate-800">{value}</h3>
        </div>
    </div>
);

const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('ร่าง') || s.includes('ตกค้าง')) return 'bg-slate-100 text-slate-700';
    if (s.includes('ผ่อนครบ') || s.includes('สำเร็จ') || s.includes('paid')) return 'bg-emerald-100 text-emerald-700';
    if (s.includes('ยกเลิก') || s.includes('cancel')) return 'bg-red-100 text-red-700';
    if (s.includes('รอตรวจสอบ') || s.includes('ตรวจสอบ')) return 'bg-amber-100 text-amber-700';
    return 'bg-blue-100 text-blue-700';
};

const getStatusDotColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('ร่าง') || s.includes('ตกค้าง')) return 'bg-slate-400';
    if (s.includes('ผ่อนครบ') || s.includes('สำเร็จ') || s.includes('paid')) return 'bg-emerald-500';
    if (s.includes('ยกเลิก') || s.includes('cancel')) return 'bg-red-500';
    if (s.includes('รอตรวจสอบ') || s.includes('ตรวจสอบ')) return 'bg-amber-500';
    return 'bg-blue-500';
};

const AgentSection = ({ name, records, isOpenInitially }) => {
    const [isOpen, setIsOpen] = useState(isOpenInitially);
    const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });

    const sortedData = useMemo(() => sortRecords(records, sort.key, sort.dir), [records, sort]);
    const totalValue = records.reduce((sum, r) => sum + (r.totalPremium || 0), 0);

    const handleSort = (key) => {
        setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden mb-4">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-6 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4 text-left">
                    <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">{name.charAt(0)}</div>
                    <div>
                        <h5 className="font-bold text-slate-800 text-lg">{name}</h5>
                        <p className="text-slate-500 text-sm">{records.length} Records • Premium: ฿{totalValue.toLocaleString()}</p>
                    </div>
                </div>
                {isOpen ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
            </button>
            {isOpen && (
                <div className="border-t border-slate-100 overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50/50 select-none">
                            <tr>
                                <SortableHeader label="เลขที่รับแจ้ง" sortKey="notificationNo" currentSort={sort} onSort={handleSort} />
                                <SortableHeader label="วันที่สร้างรายการ" sortKey="createdAt" currentSort={sort} onSort={handleSort} />
                                <SortableHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} />
                                <SortableHeader label="Product" sortKey="product" currentSort={sort} onSort={handleSort} />
                                <SortableHeader label="ทะเบียนรถ" sortKey="licensePlate" currentSort={sort} onSort={handleSort} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {sortedData.map((r, i) => (
                                <tr key={i} className="hover:bg-slate-50/30 transition-colors">
                                    <td className="px-6 py-3 text-sm font-medium text-blue-600">{r.notificationNo}</td>
                                    <td className="px-6 py-3 text-sm text-slate-600">{r.createdAt}</td>
                                    <td className="px-6 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getStatusColor(r.status)}`}>{r.status}</span></td>
                                    <td className="px-6 py-3 text-sm text-slate-600">{r.product}</td>
                                    <td className="px-6 py-3 text-sm font-mono text-slate-500">{r.licensePlate}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const StatusSection = ({ name, records, isOpenInitially }) => {
    const [isOpen, setIsOpen] = useState(isOpenInitially);
    const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });

    const sortedData = useMemo(() => sortRecords(records, sort.key, sort.dir), [records, sort]);

    const handleSort = (key) => {
        setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden mb-4">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-6 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4 text-left">
                    <span className={`w-3 h-3 rounded-full ${getStatusDotColor(name)}`}></span>
                    <div>
                        <h5 className="font-bold text-slate-800 text-lg uppercase tracking-wide">{name}</h5>
                        <p className="text-slate-500 text-sm">{records.length} Applications</p>
                    </div>
                </div>
                {isOpen ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
            </button>
            {isOpen && (
                <div className="border-t border-slate-100 overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50/50 select-none">
                            <tr>
                                <SortableHeader label="เลขที่รับแจ้ง" sortKey="notificationNo" currentSort={sort} onSort={handleSort} />
                                <SortableHeader label="วันที่สร้างรายการ" sortKey="createdAt" currentSort={sort} onSort={handleSort} />
                                <SortableHeader label="Agent" sortKey="agentName" currentSort={sort} onSort={handleSort} />
                                <SortableHeader label="Premium" sortKey="totalPremium" currentSort={sort} onSort={handleSort} />
                                <SortableHeader label="Company" sortKey="insuranceCompany" currentSort={sort} onSort={handleSort} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {sortedData.map((r, i) => (
                                <tr key={i} className="hover:bg-slate-50/30 transition-colors">
                                    <td className="px-6 py-3 text-sm font-medium text-blue-600">{r.notificationNo}</td>
                                    <td className="px-6 py-3 text-sm text-slate-600">{r.createdAt}</td>
                                    <td className="px-6 py-3 text-sm text-slate-800 font-medium">{r.agentName}</td>
                                    <td className="px-6 py-3 text-sm font-semibold text-emerald-600">฿{(r.totalPremium || 0).toLocaleString()}</td>
                                    <td className="px-6 py-3 text-sm text-slate-500 truncate max-w-[150px]">{r.insuranceCompany}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const ContractStatus = () => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [records, setRecords] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSync, setLastSync] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const [selectedStatuses, setSelectedStatuses] = useState([]);

    const uniqueStatuses = useMemo(() => {
        const statuses = new Set();
        records.forEach(r => {
            if (r.status) statuses.add(r.status);
        });
        return Array.from(statuses).sort();
    }, [records]);

    // Set default selection based on requested statuses
    const setDefaults = useCallback(() => {
        const defaultSelection = uniqueStatuses.filter(s =>
            DEFAULT_STATUS_SELECTION.includes(s)
        );
        setSelectedStatuses(defaultSelection);
    }, [uniqueStatuses]);

    useEffect(() => {
        if (uniqueStatuses.length > 0 && selectedStatuses.length === 0) {
            setDefaults();
        }
    }, [uniqueStatuses, setDefaults, selectedStatuses.length]);

    const processGvizJSON = useCallback((jsonStr) => {
        try {
            const startIdx = jsonStr.indexOf('{');
            const endIdx = jsonStr.lastIndexOf('}');
            const data = JSON.parse(jsonStr.substring(startIdx, endIdx + 1));
            if (data.status === 'error') throw new Error(data.errors[0].detailed_message || 'API error');
            const table = data.table;
            const indexMap = {};
            table.cols.forEach((col, idx) => {
                if (COLUMN_MAPPING[col.label]) indexMap[idx] = COLUMN_MAPPING[col.label];
            });
            const parsed = table.rows.map((row) => {
                const record = {};
                row.c.forEach((cell, idx) => {
                    const prop = indexMap[idx];
                    if (prop) {
                        let value = cell ? cell.v : null;
                        if (['totalPremium', 'netPremium', 'actualSellingPrice', 'firstInstallment', 'installmentAmount', 'amount'].includes(prop)) {
                            record[prop] = parseFloat(String(value || '0').replace(/,/g, '')) || 0;
                        } else if (['installments', 'installmentCount'].includes(prop)) {
                            record[prop] = parseInt(String(value || '0')) || 0;
                        } else {
                            record[prop] = value === null ? '' : String(value);
                        }
                    }
                });
                return record;
            });
            setRecords(parsed);
            setLastSync(new Date().toLocaleTimeString());
            setErrorMessage(null);
        } catch (err) {
            setErrorMessage(`Failed to parse: ${err.message}`);
        }
    }, []);

    const fetchFromGoogleSheet = useCallback(async () => {
        setIsSyncing(true);
        try {
            const response = await fetch(GOOGLE_SHEET_JSON_URL);
            if (!response.ok) throw new Error('Cloud error');
            const text = await response.text();
            if (text.includes('<!DOCTYPE html>')) throw new Error('Access Denied. Publish sheet to web.');
            processGvizJSON(text);
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setIsSyncing(false);
        }
    }, [processGvizJSON]);

    // Core Filtering Logic
    const filteredBaseRecords = useMemo(() => {
        return records.filter(r => {
            const matchesSearch = (r.notificationNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (r.agentName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (r.status || '').toLowerCase().includes(searchTerm.toLowerCase());

            const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(r.status);

            return matchesSearch && statusMatch;
        });
    }, [records, searchTerm, selectedStatuses]);

    // Grouping for reports
    const agentGroups = useMemo(() => {
        const groups = {};
        filteredBaseRecords.forEach(r => {
            const name = r.agentName || 'ไม่ระบุ';
            if (!groups[name]) groups[name] = [];
            groups[name].push(r);
        });
        return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
    }, [filteredBaseRecords]);

    const statusGroups = useMemo(() => {
        const groups = {};
        filteredBaseRecords.forEach(r => {
            const status = r.status || 'ไม่ระบุ';
            if (!groups[status]) groups[status] = [];
            groups[status].push(r);
        });
        return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
    }, [filteredBaseRecords]);

    const totalPremiumValue = useMemo(() => records.reduce((acc, curr) => acc + (curr.totalPremium || 0), 0), [records]);
    const statusChartData = useMemo(() => statusGroups.map(([name, data]) => ({ name, value: data.length })), [statusGroups]);
    const agentChartData = useMemo(() => agentGroups.slice(0, 5).map(([name, data]) => ({ name, count: data.length })), [agentGroups]);

    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

    const toggleStatus = (status) => {
        setSelectedStatuses(prev =>
            prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
        );
    };

    const selectAllStatuses = () => setSelectedStatuses(uniqueStatuses);
    const deselectAllStatuses = () => setSelectedStatuses([]);

    return (
        <div className="flex min-h-screen text-slate-800">
            <aside className="w-64 bg-slate-900 text-white flex flex-col fixed h-full shadow-2xl z-10" style={{ top: 0, left: 0, bottom: 0 }}>
                <div className="p-6 flex items-center gap-3 border-b border-slate-800">
                    <ShieldCheck className="text-blue-400" size={32} />
                    <h1 className="font-bold text-xl">InsureTrack Pro</h1>
                </div>
                <nav className="flex-1 mt-6 px-4 space-y-2">
                    {[
                        { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
                        { id: 'agent-report', icon: Users, label: 'Report by Agent' },
                        { id: 'status-report', icon: PieChartIcon, label: 'Report by Status' },
                        { id: 'all-records', icon: FileText, label: 'All Records' }
                    ].map(item => (
                        <button key={item.id} onClick={() => setActiveTab(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${activeTab === item.id ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                            <item.icon size={20} /> {item.label}
                        </button>
                    ))}
                </nav>
                <div className="p-4 border-t border-slate-800 space-y-3">
                    <button onClick={fetchFromGoogleSheet} disabled={isSyncing} className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white font-semibold py-3 rounded-lg transition-all shadow-md">
                        <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} /> {isSyncing ? 'Syncing...' : 'Update from Cloud'}
                    </button>
                    {lastSync && <p className="text-[10px] text-slate-500 text-center uppercase tracking-widest font-bold">Last Sync: {lastSync}</p>}
                </div>
            </aside>

            <main className="flex-1 p-8 ml-64 bg-slate-50 min-h-screen">
                <header className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-3xl font-bold text-slate-800">
                            {activeTab === 'dashboard' ? 'Executive Summary' :
                                activeTab === 'agent-report' ? 'Agent Performance Report' :
                                    activeTab === 'status-report' ? 'Status Distribution Report' : 'Insurance Record Explorer'}
                        </h2>
                        <div className="flex items-center gap-2 mt-1">
                            <p className="text-slate-500 text-sm font-medium">Real-time Insurance Dashboard</p>
                            <span className="text-slate-300">•</span>
                            <a href={`https://docs.google.com/spreadsheets/d/${SHEET_ID}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-xs flex items-center gap-1 hover:underline">Open Google Sheet <ExternalLink size={12} /></a>
                        </div>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input type="text" placeholder="Search records..." className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64 shadow-sm" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                </header>

                {(activeTab === 'agent-report' || activeTab === 'status-report' || activeTab === 'all-records') && records.length > 0 && (
                    <div className="mb-6 bg-white p-5 rounded-xl border border-slate-100 shadow-sm space-y-4 animate-in fade-in duration-300">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-slate-700 font-bold">
                                <Filter size={18} className="text-blue-500" />
                                <span>Filter by Policy Status</span>
                            </div>
                            <div className="flex gap-4">
                                <button onClick={setDefaults} className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 uppercase tracking-wider transition-colors">
                                    <RotateCcw size={14} /> Reset Defaults
                                </button>
                                <button onClick={selectAllStatuses} className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 uppercase tracking-wider transition-colors">
                                    <CheckCircle2 size={14} /> Select All
                                </button>
                                <button onClick={deselectAllStatuses} className="text-xs font-bold text-slate-400 hover:text-slate-600 flex items-center gap-1 uppercase tracking-wider transition-colors">
                                    <XCircle size={14} /> Clear
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {uniqueStatuses.map(status => (
                                <button
                                    key={status}
                                    onClick={() => toggleStatus(status)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border flex items-center gap-2 ${selectedStatuses.includes(status)
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm transform scale-105'
                                        : 'bg-white text-slate-500 border-slate-200 hover:border-blue-400 hover:text-blue-500'
                                        }`}
                                >
                                    <span className={`w-2 h-2 rounded-full ${selectedStatuses.includes(status) ? 'bg-white' : getStatusDotColor(status)}`}></span>
                                    {status}
                                </button>
                            ))}
                        </div>
                        {selectedStatuses.length === 0 && (
                            <p className="text-xs text-amber-600 font-medium italic flex items-center gap-1">
                                <AlertCircle size={14} /> No statuses selected. Showing no data.
                            </p>
                        )}
                        <div className="pt-2 border-t border-slate-50 flex items-center gap-2">
                            <p className="text-[10px] text-slate-400 uppercase font-black tracking-[0.2em]">Current Filter:</p>
                            <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded font-bold uppercase">
                                {selectedStatuses.length === uniqueStatuses.length ? "Showing All" : `Showing ${selectedStatuses.length} Statuses`}
                            </span>
                        </div>
                    </div>
                )}

                {errorMessage && (
                    <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                        <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={20} />
                        <div>
                            <h4 className="font-bold text-red-800 uppercase text-xs tracking-wider">Error</h4>
                            <p className="text-red-700 text-sm leading-relaxed mt-1">{errorMessage}</p>
                        </div>
                    </div>
                )}

                {records.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[60vh] text-center bg-white border border-slate-100 rounded-3xl mx-4 shadow-sm">
                        <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6 text-blue-400 animate-pulse"><RefreshCw size={48} /></div>
                        <h3 className="text-2xl font-bold text-slate-700">Import Google Sheet Data</h3>
                        <button onClick={fetchFromGoogleSheet} className="bg-blue-600 hover:bg-blue-500 text-white mt-8 px-10 py-4 rounded-full font-bold shadow-xl transition-all flex items-center gap-2 transform active:scale-95"><RefreshCw size={20} /> Update from Cloud</button>
                    </div>
                ) : (
                    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {activeTab === 'dashboard' && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                    <SummaryCard title="Total Records" value={records.length} icon={FileText} color="bg-blue-500" />
                                    <SummaryCard title="Total Agents" value={agentGroups.length} icon={Users} color="bg-indigo-500" />
                                    <SummaryCard title="Total Premium" value={`฿${totalPremiumValue.toLocaleString()}`} icon={ShieldCheck} color="bg-emerald-500" />
                                    <SummaryCard title="Unique Statuses" value={uniqueStatuses.length} icon={PieChartIcon} color="bg-orange-500" />
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                                        <h4 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2"><PieChartIcon className="text-slate-400" size={18} />Status Distribution (Filtered)</h4>
                                        <div className="h-64">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie data={statusChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                                        {statusChartData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                                    </Pie>
                                                    <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                                                    <Legend verticalAlign="bottom" height={36} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                                        <h4 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2"><Users className="text-slate-400" size={18} />Top 5 Active Agents (Filtered)</h4>
                                        <div className="h-64">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={agentChartData}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                    <XAxis dataKey="name" fontSize={10} axisLine={false} tickLine={false} />
                                                    <YAxis fontSize={10} axisLine={false} tickLine={false} />
                                                    <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                                                    <Bar dataKey="count" fill="#3b82f6" radius={[6, 6, 0, 0]} barSize={40} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'agent-report' && agentGroups.length > 0 ? (
                            agentGroups.map(([name, data], idx) => (
                                <AgentSection key={name} name={name} records={data} isOpenInitially={idx === 0} />
                            ))
                        ) : activeTab === 'agent-report' && (
                            <div className="p-20 text-center bg-white rounded-3xl border border-dashed border-slate-200 text-slate-400">
                                <Users size={48} className="mx-auto mb-4 opacity-20" />
                                No agent data matches the selected filters.
                            </div>
                        )}

                        {activeTab === 'status-report' && statusGroups.length > 0 ? (
                            statusGroups.map(([name, data], idx) => (
                                <StatusSection key={name} name={name} records={data} isOpenInitially={idx === 0} />
                            ))
                        ) : activeTab === 'status-report' && (
                            <div className="p-20 text-center bg-white rounded-3xl border border-dashed border-slate-200 text-slate-400">
                                <PieChartIcon size={48} className="mx-auto mb-4 opacity-20" />
                                No status data matches the selected filters.
                            </div>
                        )}

                        {activeTab === 'all-records' && (
                            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-slate-50 border-b border-slate-200">
                                            <tr>
                                                <th className="px-6 py-4 font-bold text-slate-400 text-[10px] uppercase tracking-widest">เลขที่รับแจ้ง</th>
                                                <th className="px-6 py-4 font-bold text-slate-400 text-[10px] uppercase tracking-widest">วันที่สร้างรายการ</th>
                                                <th className="px-6 py-4 font-bold text-slate-400 text-[10px] uppercase tracking-widest">Agent</th>
                                                <th className="px-6 py-4 font-bold text-slate-400 text-[10px] uppercase tracking-widest">Status</th>
                                                <th className="px-6 py-4 font-bold text-slate-400 text-[10px] uppercase tracking-widest text-right">Premium</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {filteredBaseRecords.map((r, i) => (
                                                <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-6 py-4 font-medium text-blue-600 text-sm">{r.notificationNo}</td>
                                                    <td className="px-6 py-4 text-slate-600 text-xs">{r.createdAt}</td>
                                                    <td className="px-6 py-4 text-slate-800 text-sm font-semibold">{r.agentName}</td>
                                                    <td className="px-6 py-4"><span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${getStatusColor(r.status)}`}>{r.status}</span></td>
                                                    <td className="px-6 py-4 font-bold text-slate-900 text-sm text-right">฿{(r.totalPremium || 0).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {filteredBaseRecords.length === 0 && (
                                        <div className="p-20 text-center text-slate-400">No records found matching filters.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default ContractStatus;
